import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ActionBar,
  ActionBarDivider,
  ActionBarResetButton,
  DateRangeFilter,
  MultiSelect,
  MultiSelectOption,
  MultiSelectSection,
  SearchInput,
} from '@klareospace/pharos-lib-web-utils';

import type { Order } from '@klareospace/pharos-lib-types';
import { UNASSIGNED_DRIVER_VALUE } from '../types/order.types';
import type { OrderTab, StatusBucketKey } from '../types/order.types';
import type { DataTableColumn, DateRangeValue } from '@klareospace/pharos-lib-web-utils';
import { ColumnsToggle } from '@klareospace/pharos-lib-web-utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export type OrdersActionBarProps = {
  activeTab: OrderTab;
  canListDrivers: boolean;
  canResetColumns: boolean;
  carrierFilter: string[];
  carrierOptions: MultiSelectOption[];
  columns: DataTableColumn<Order>[];
  dateRange: DateRangeValue;
  driverActiveOptions: MultiSelectOption[];
  driverExtraSelectedOptions: MultiSelectOption[];
  driverFilter: string[];
  driverHiddenCount: number;
  driverInactiveOptions: MultiSelectOption[];
  hiddenColumns: string[];
  isDriverOptionsLoading: boolean;
  isDriverSearchActive: boolean;
  onCarrierChange: (values: string[]) => void;
  onDateRangeChange: (value: DateRangeValue) => void;
  onDriverChange: (values: string[]) => void;
  onDriverDropdownOpenChange: (open: boolean) => void;
  onDriverSearchChange: (value: string) => void;
  onOrderTypeChange: (values: string[]) => void;
  onReset: () => void;
  onResetColumns: () => void;
  onSaveColumns: () => void;
  onSearchChange: (value: string) => void;
  onShipperChange: (values: string[]) => void;
  onStatusBucketChange: (values: StatusBucketKey[]) => void;
  onToggleColumn: (columnKey: string) => void;
  orderTypeFilter: string[];
  orderTypeOptions: MultiSelectOption[];
  searchValue: string;
  shipperFilter: string[];
  shipperOptions: MultiSelectOption[];
  showCarrierFilter: boolean;
  statusBucketFilter: StatusBucketKey[];
};

// ─── Component ────────────────────────────────────────────────────────────────

export function OrdersActionBar({
  activeTab,
  canListDrivers,
  canResetColumns,
  carrierFilter,
  carrierOptions,
  columns,
  dateRange,
  driverActiveOptions,
  driverExtraSelectedOptions,
  driverFilter,
  driverHiddenCount,
  driverInactiveOptions,
  hiddenColumns,
  isDriverOptionsLoading,
  isDriverSearchActive,
  onCarrierChange,
  onDateRangeChange,
  onDriverChange,
  onDriverDropdownOpenChange,
  onDriverSearchChange,
  onOrderTypeChange,
  onReset,
  onResetColumns,
  onSaveColumns,
  onSearchChange,
  onShipperChange,
  onStatusBucketChange,
  onToggleColumn,
  orderTypeFilter,
  orderTypeOptions,
  searchValue,
  shipperFilter,
  shipperOptions,
  showCarrierFilter,
  statusBucketFilter,
}: OrdersActionBarProps): React.JSX.Element {
  const { t } = useTranslation('orders');

  const statusBucketOptions = useMemo(
    () =>
      ['PENDING', 'TODO', 'IN_PROGRESS', 'DONE'].map((key) => ({
        label: t(`actionBar.filters.statusBuckets.${key}`),
        value: key,
      })),
    [t],
  );

  const driverSections = useMemo<MultiSelectSection[]>(() => {
    const namedSections = [
      { heading: t('actionBar.filters.driver.sections.active'), options: driverActiveOptions },
      { heading: t('actionBar.filters.driver.sections.inactive'), options: driverInactiveOptions },
    ];
    // Hidden while searching, otherwise cmdk always counts >=1 item and the empty state never shows.
    if (isDriverSearchActive) return namedSections;
    const pinnedUnassigned = {
      options: [{ label: t('actionBar.filters.driver.unassigned'), value: UNASSIGNED_DRIVER_VALUE }],
    };
    return [pinnedUnassigned, ...namedSections];
  }, [t, driverActiveOptions, driverInactiveOptions, isDriverSearchActive]);

  const driverResultsHint =
    driverHiddenCount > 0 ? t('actionBar.filters.driver.searchMore', { count: driverHiddenCount }) : undefined;

  function handleStatusBucketChange(values: string[]): void {
    onStatusBucketChange(values as StatusBucketKey[]);
  }

  return (
    <ActionBar className="flex-nowrap items-start">
      <div className="flex flex-1 flex-wrap items-center gap-1.5">
        <SearchInput
          ariaLabel={t('actionBar.search.ariaLabel')}
          className="w-[200px]"
          onChange={onSearchChange}
          placeholder={t('actionBar.search.placeholder')}
          value={searchValue}
        />
        <ActionBarDivider />
        <DateRangeFilter
          label={t('actionBar.filters.date')}
          onChange={onDateRangeChange}
          showTimeRange
          value={dateRange}
        />
        {activeTab === 'all' && (
          <MultiSelect
            className="h-9 w-auto min-w-[11rem] gap-1.5 border border-input bg-background px-3 text-sm font-normal shadow-none"
            defaultValue={statusBucketFilter}
            hideClear
            hideClose
            label={t('actionBar.filters.status')}
            maxCount={1}
            onValueChange={handleStatusBucketChange}
            options={statusBucketOptions}
            placeholder={t('actionBar.filters.all')}
            searchable={false}
            showLabelWhenFiltering
            variant="compact"
          />
        )}
        {showCarrierFilter && (
          <MultiSelect
            className="h-9 w-auto min-w-[11rem] gap-1.5 border border-input bg-background px-3 text-sm font-normal shadow-none"
            defaultValue={carrierFilter}
            hideClear
            hideClose
            label={t('actionBar.filters.carrier')}
            maxCount={2}
            onValueChange={onCarrierChange}
            options={carrierOptions}
            placeholder={t('actionBar.filters.all')}
            searchable
            showLabelWhenFiltering
            variant="compact"
          />
        )}
        {canListDrivers && (
          <MultiSelect
            async
            className="h-9 w-auto min-w-[11rem] gap-1.5 border border-input bg-background px-3 text-sm font-normal shadow-none"
            defaultValue={driverFilter}
            emptyText={t('actionBar.filters.driver.emptyText')}
            extraSelectedOptions={driverExtraSelectedOptions}
            hideClear
            hideClose
            isLoading={isDriverOptionsLoading}
            label={t('actionBar.filters.driver.label')}
            loadingText={t('actionBar.filters.driver.loadingText')}
            maxCount={2}
            onOpenChange={onDriverDropdownOpenChange}
            onSearchValueChange={onDriverSearchChange}
            onValueChange={onDriverChange}
            placeholder={t('actionBar.filters.all')}
            resultsHint={driverResultsHint}
            searchPlaceholder={t('actionBar.filters.driver.searchPlaceholder')}
            searchPrompt={t('actionBar.filters.driver.searchPrompt')}
            searchable
            sections={driverSections}
            showLabelWhenFiltering
            variant="compact"
          />
        )}
        <MultiSelect
          className="h-9 w-auto min-w-[11rem] gap-1.5 border border-input bg-background px-3 text-sm font-normal shadow-none"
          defaultValue={shipperFilter}
          hideClear
          hideClose
          label={t('actionBar.filters.shipper')}
          maxCount={2}
          onValueChange={onShipperChange}
          options={shipperOptions}
          placeholder={t('actionBar.filters.all')}
          searchable
          showLabelWhenFiltering
          variant="compact"
        />
        <MultiSelect
          className="h-9 w-auto min-w-[11rem] gap-1.5 border border-input bg-background px-3 text-sm font-normal shadow-none"
          defaultValue={orderTypeFilter}
          hideClear
          hideClose
          label={t('actionBar.filters.orderType')}
          maxCount={2}
          onValueChange={onOrderTypeChange}
          options={orderTypeOptions}
          placeholder={t('actionBar.filters.all')}
          searchable={false}
          showLabelWhenFiltering
          variant="compact"
        />
        <ActionBarResetButton onClick={onReset} />
      </div>
      <ColumnsToggle
        canReset={canResetColumns}
        columns={columns}
        hiddenColumns={hiddenColumns}
        onClose={onSaveColumns}
        resetToDefault={onResetColumns}
        toggleColumn={onToggleColumn}
      />
    </ActionBar>
  );
}
